import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, X, Send, Loader2, Maximize2, Minimize2, Plus, Trash2, Sparkles, Settings, Store, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "../lib/api";
import sparkChatIcon from "@/assets/sparkchat-icon.png";


const SparkChat = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [pendingStore, setPendingStore] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      loadSessions();
      loadStores();
    }
  }, [isOpen, isAuthenticated]);

  const loadStores = async () => {
    try {
      const response = await api.get(`/settings/stores`);
      const loaded = response.data.stores || [];
      setStores(loaded);
      if (loaded.length === 1) {
        setSelectedStore(loaded[0]);
        setPendingStore(loaded[0]);
      }
    } catch (error) {
      console.error("Failed to load stores:", error);
    }
  };

  const openStoreModal = () => {
    setPendingStore(selectedStore);
    setShowStoreModal(true);
  };

  const confirmStoreSelection = () => {
    setSelectedStore(pendingStore);
    setShowStoreModal(false);
  };

  const loadSessions = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/chat/sessions`);
      setSessions(response.data);
      if (response.data.length > 0 && !currentSession) {
        await loadSession(response.data[0].id);
      }
    } catch (error) {
      console.error("Failed to load chat sessions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const response = await api.get(`/chat/sessions/${sessionId}`);
      setCurrentSession(response.data);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error("Failed to load session:", error);
    }
  };

  const createNewSession = async () => {
    try {
      const response = await api.post(`/chat/sessions`, {});
      setSessions([response.data, ...sessions]);
      setCurrentSession(response.data);
      setMessages([]);
    } catch (error) {
      console.error("Failed to create session:", error);
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      await api.delete(`/chat/sessions/${sessionId}`);
      setSessions(sessions.filter(s => s.id !== sessionId));
      if (currentSession?.id === sessionId) {
        setCurrentSession(null);
        setMessages([]);
      }
    } catch (error) {
      console.error("Failed to delete session:", error);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isSending) return;
    
    const userMessage = inputValue.trim();
    setInputValue("");
    
    const tempUserMessage = { id: "temp-user", role: "user", content: userMessage };
    setMessages(prev => [...prev, tempUserMessage]);
    
    setIsSending(true);
    
    try {
      let sessionId = currentSession?.id;
      
      if (!sessionId) {
        const newSessionResponse = await api.post(`/chat/sessions`, {});
        const newSession = newSessionResponse.data;
        sessionId = newSession.id;
        setCurrentSession(newSession);
        setSessions(prev => [newSession, ...prev]);
      }
      
      const response = await api.post(
        `/chat/sessions/${sessionId}/messages`,
        { message: userMessage, store_id: selectedStore?.id }
      );
      
      const assistantMessage = { id: `assistant-${Date.now()}`, role: "assistant", content: response.data.response };
      setMessages(prev => [...prev.filter(m => m.id !== "temp-user"), 
        { ...tempUserMessage, id: `user-${Date.now()}` }, 
        assistantMessage
      ]);

      if (response.data.navigate_to) {
        const path = response.data.navigate_to;
        setTimeout(() => {
          setIsOpen(false);
          navigate(path);
        }, 1200);
      }
      
      loadSessions();
    } catch (error) {
      console.error("Failed to send message:", error);
      setMessages(prev => [...prev, { id: "error", role: "assistant", content: "Sorry, I encountered an error. Please try again." }]);
    } finally {
      setIsSending(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isAuthenticated) return null;

  const panelClasses = isExpanded
    ? "fixed inset-4 z-50 flex flex-col rounded-xl shadow-2xl"
    : "fixed bottom-4 right-4 left-4 sm:left-auto z-40 w-auto sm:w-96 h-[min(500px,70vh)] flex flex-col rounded-xl shadow-2xl";

  return (
    <div data-sparkchat>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-4 z-40 p-2 bg-transparent rounded-full shadow-lg hover:scale-105 transition-all"
          title="Open SparkChat"
        >
          <img src={sparkChatIcon} alt="SparkChat" className="w-12 h-12" />
        </button>
      )}

      {isOpen && (
        <div className={`${panelClasses} bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700`}>
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-600 to-teal-600 rounded-t-xl">
            <div className="flex items-center space-x-2">
              <img src={sparkChatIcon} alt="SparkChat" className="w-6 h-6 brightness-0 invert" />
              <span className="font-semibold text-white">SparkChat</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-white/80 max-w-[100px] truncate">
                {selectedStore?.store_name || "All Stores"}
              </span>
              <button
                onClick={openStoreModal}
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded transition-colors"
                title="Select Store"
              >
                <Settings className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded transition-colors"
                title={isExpanded ? "Minimize" : "Expand"}
              >
                {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {isExpanded && (
              <div className="w-64 border-r border-gray-200 dark:border-gray-700 flex flex-col bg-gray-50 dark:bg-gray-800/50">
                <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <Button
                    onClick={createNewSession}
                    size="sm"
                    className="w-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" /> New Chat
                  </Button>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                  {sessions.map(session => (
                    <div
                      key={session.id}
                      className={`group flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                        currentSession?.id === session.id
                          ? "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300"
                          : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }`}
                      onClick={() => loadSession(session.id)}
                    >
                      <span className="truncate text-sm flex-1">{session.title || "New Chat"}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteSession(session.id); }}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-opacity"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex-1 flex flex-col">
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {isLoading && !messages.length ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center px-4">
                    <img src={sparkChatIcon} alt="SparkChat" className="w-20 h-20 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">Welcome to SparkChat!</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      Ask me anything about your store, account, credits, jobs, or say "take me to..." any section.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {["What's my inventory status?", "What's my credit balance?", "Take me to BuyBox", "What jobs ran recently?"].map((suggestion, i) => (
                        <button
                          key={i}
                          onClick={() => { setInputValue(suggestion); }}
                          className="text-xs px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full hover:bg-teal-100 dark:hover:bg-teal-900/30 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-2xl ${
                          msg.role === "user"
                            ? "bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-br-md"
                            : "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-bl-md"
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  ))
                )}
                {isSending && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-2xl rounded-bl-md">
                      <div className="flex items-center space-x-2">
                        <Loader2 className="w-4 h-4 animate-spin text-teal-500" />
                        <span className="text-sm text-gray-500 dark:text-gray-400">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={inputValue}
                    ref={inputRef}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask anything or say 'take me to...'"
                    className="flex-1 px-4 py-2 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    disabled={isSending}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!inputValue.trim() || isSending}
                    className="p-2 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-full hover:from-blue-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showStoreModal} onOpenChange={setShowStoreModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="w-5 h-5 text-teal-500" />
              Select Store
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-4">
            {stores.length !== 1 && (
              <button
                onClick={() => setPendingStore(null)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors ${
                  pendingStore === null 
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' 
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <span className="text-gray-900 dark:text-white font-medium">All Stores</span>
                {pendingStore === null && <Check className="w-5 h-5 text-teal-500" />}
              </button>
            )}
            {Array.isArray(stores) && stores.map(store => (
              <button
                key={store.id}
                onClick={() => setPendingStore(store)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors ${
                  pendingStore?.id === store.id 
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' 
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <span className="text-gray-900 dark:text-white">{store.store_name}</span>
                {pendingStore?.id === store.id && <Check className="w-5 h-5 text-teal-500" />}
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowStoreModal(false)}>
              Cancel
            </Button>
            <Button onClick={confirmStoreSelection} className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white">
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SparkChat;
